import { APP_NAME } from '../utils/branding';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaExternalLinkAlt, FaPrint, FaQrcode } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { ownsEvents } from '../utils/roles';
import { getEvent, getEventShareLink } from '../services/api';

const buildQrImageUrl = (value, size = 360) => (
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`
);

const EventQRCode = () => {
  const { eventId } = useParams();
  const { user, loading } = useAuth();
  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState('');
  const [sharePath, setSharePath] = useState('');

  // A private event's link must carry its share token, otherwise scanning
  // the QR code drops the guest straight onto the access-denied guard.
  const scanUrl = useMemo(
    () => `${window.location.origin}${sharePath || `/event/${eventId}/access`}`,
    [eventId, sharePath]
  );
  const qrImageUrl = useMemo(() => buildQrImageUrl(scanUrl), [scanUrl]);
  const eventName = event?.name || `Événement ${eventId}`;
  const photographerName = event?.photographer?.name || user?.name || `Photographe ${APP_NAME}`;

  useEffect(() => {
    const fetchEvent = async () => {
      setEventLoading(true);
      setEventError('');

      try {
        const response = await getEvent(eventId);
        setEvent(response.event || response.data?.event || response);

        const share = await getEventShareLink(eventId);
        if (share?.path) setSharePath(share.path);
      } catch (err) {
        setEventError(err.message || "Impossible de charger les détails de l’événement.");
      } finally {
        setEventLoading(false);
      }
    };

    if (!loading && ownsEvents(user)) {
      fetchEvent();
    }
  }, [eventId, loading, user]);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!ownsEvents(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container qr-page">
      <div className="qr-toolbar">
        <Link to="/photographer" className="qr-link-button no-print">
          <FaArrowLeft /> Retour
        </Link>
        <div className="qr-actions no-print">
          <a className="qr-link-button" href={qrImageUrl} download={`event-${eventId}-qr.png`}>
            <FaDownload /> QR image
          </a>
          <button type="button" className="qr-primary-button" onClick={() => window.print()}>
            <FaPrint /> Generer le PDF
          </button>
        </div>
      </div>

      <section className="qr-sheet">
        <div className="qr-sheet-header">
          <FaQrcode />
          <div>
            <h1>{eventLoading ? APP_NAME : eventName}</h1>
            <p>Photos par {photographerName}</p>
          </div>
        </div>

        {eventError && (
          <div className="error-message no-print" style={{ marginBottom: '1.5rem' }}>
            {eventError}
          </div>
        )}

        <div className="qr-code-frame">
          <img src={qrImageUrl} alt={`QR code ${eventName}`} />
        </div>

        <div className="qr-event-meta">
          <strong>{eventName}</strong>
          <span>{photographerName}</span>
        </div>

        <div className="qr-instructions">
          <h2>Comment acceder a vos photos</h2>
          <p>Scannez ce QR code avec votre téléphone. Connectez-vous si vous avez déjà un compte, ou créez-en un. Après connexion, vous arriverez directement sur les photos de cet événement.</p>
        </div>

        <div className="qr-url no-print">
          <span>{scanUrl}</span>
          <a href={scanUrl} target="_blank" rel="noreferrer" title="Tester le lien">
            <FaExternalLinkAlt />
          </a>
        </div>
      </section>
    </div>
  );
};

export default EventQRCode;
